import React, { useEffect, useMemo, useRef, useState } from "react";
import { View, StyleSheet } from "react-native";
import { autoUpdate, flip, offset as floatingOffset, shift, useFloating } from "@floating-ui/react";
import { Portal } from "react-native-paper";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import CustomText from "../general/CustomText/CustomText";

const TOOLTIP_ANIMATION_DURATION_MS = 120;
const TOOLTIP_ANIMATION_DISTANCE = 4;
const TOOLTIP_SHOW_DELAY_MS = 150;

const getHiddenTranslateStyle = (placement) => {
  if (placement.startsWith("top")) {
    return { transform: [{ translateX: 0 }, { translateY: TOOLTIP_ANIMATION_DISTANCE }] };
  }

  if (placement.startsWith("bottom")) {
    return { transform: [{ translateX: 0 }, { translateY: -TOOLTIP_ANIMATION_DISTANCE }] };
  }

  if (placement.startsWith("left")) {
    return { transform: [{ translateX: TOOLTIP_ANIMATION_DISTANCE }, { translateY: 0 }] };
  }

  return { transform: [{ translateX: -TOOLTIP_ANIMATION_DISTANCE }, { translateY: 0 }] };
};

const getArrowStyle = (placement) => {
  if (placement.startsWith("bottom")) {
    return styles.triangleUp;
  }

  if (placement.startsWith("right")) {
    return styles.triangleLeft;
  }

  if (placement.startsWith("left")) {
    return styles.triangleRight;
  }

  return styles.triangleDown;
};

const wrapTextByLength = (value, maxLineLength) => {
  if (typeof value !== "string" || !Number.isFinite(maxLineLength) || maxLineLength <= 0) {
    return value;
  }

  const words = value.trim().split(/\s+/);
  const lines = [];
  let currentLine = "";

  words.forEach((word) => {
    if (word.length > maxLineLength) {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = "";
      }

      for (let index = 0; index < word.length; index += maxLineLength) {
        lines.push(word.slice(index, index + maxLineLength));
      }
      return;
    }

    const nextLine = currentLine ? `${currentLine} ${word}` : word;
    if (nextLine.length > maxLineLength && currentLine) {
      lines.push(currentLine);
      currentLine = word;
      return;
    }

    currentLine = nextLine;
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines.join("\n");
};

const Tooltip = ({
  children,
  content,
  offset = 10,
  horizontalOffset = 0,
  maxWidth = 230,
  showArrow = true,
  placement = "top",
  showDelay = TOOLTIP_SHOW_DELAY_MS,
  textNumberOfLines = 1,
  wrapText = false,
  wrapTextAfterLength,
  tooltipStyle,
  tooltipTextStyle,
  showOnFocus = true,
  showOnPress = false,
  enableOnSmallScreens = false,
}) => {
  const { isMedium } = useResponsiveScreen();
  const [visible, setVisible] = useState(false);
  const [isTooltipMounted, setIsTooltipMounted] = useState(false);
  /** When content is falsy, render children only (no tooltip on hover). */
  const isTooltipEnabled = (isMedium || enableOnSmallScreens) && content;
  const isHorizontalPlacement = placement.startsWith("right") || placement.startsWith("left");
  const showDelayTimeoutRef = useRef(null);
  const enterTimeoutRef = useRef(null);

  const clearTimers = () => {
    if (showDelayTimeoutRef.current) {
      clearTimeout(showDelayTimeoutRef.current);
      showDelayTimeoutRef.current = null;
    }
    if (enterTimeoutRef.current) {
      clearTimeout(enterTimeoutRef.current);
      enterTimeoutRef.current = null;
    }
  };

  const floatingOffsetConfig = useMemo(() => {
    if (isHorizontalPlacement) {
      return { mainAxis: Math.abs(horizontalOffset), crossAxis: 0 };
    }

    return { mainAxis: Math.abs(offset), crossAxis: horizontalOffset };
  }, [isHorizontalPlacement, offset, horizontalOffset]);

  const {
    refs,
    x,
    y,
    strategy,
    placement: computedPlacement,
  } = useFloating({
    placement,
    middleware: [floatingOffset(floatingOffsetConfig), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const hiddenTranslateStyle = getHiddenTranslateStyle(computedPlacement);
  const arrowStyle = getArrowStyle(computedPlacement);
  const shouldWrapText =
    wrapText ||
    (typeof wrapTextAfterLength === "number" && typeof content === "string" && content.length > wrapTextAfterLength);
  const tooltipContent =
    shouldWrapText && typeof wrapTextAfterLength === "number"
      ? wrapTextByLength(content, wrapTextAfterLength)
      : content;
  const tooltipTextProps = shouldWrapText
    ? {}
    : {
        ellipsizeMode: "tail",
        numberOfLines: textNumberOfLines,
      };
  const tooltipWidthStyle = { maxWidth, minWidth: 0 };

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, []);

  const showTooltip = () => {
    clearTimers();

    showDelayTimeoutRef.current = setTimeout(() => {
      setIsTooltipMounted(true);
      setVisible(false);

      enterTimeoutRef.current = setTimeout(() => {
        setVisible(true);
        enterTimeoutRef.current = null;
      }, 16);

      showDelayTimeoutRef.current = null;
    }, showDelay);
  };

  const hideTooltip = () => {
    clearTimers();
    setVisible(false);
    setIsTooltipMounted(false);
  };

  const toggleTooltip = () => {
    if (!showOnPress) {
      return;
    }

    if (isTooltipMounted) {
      hideTooltip();
      return;
    }

    showTooltip();
  };

  if (!isTooltipEnabled) {
    return <View>{children}</View>;
  }

  return (
    <View>
      <View
        ref={refs.setReference}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showOnFocus ? showTooltip : undefined}
        onBlur={showOnFocus ? hideTooltip : undefined}
        onTouchStart={showOnPress ? toggleTooltip : undefined}>
        {children}
      </View>
      {isTooltipMounted && (
        <Portal>
          <View
            ref={refs.setFloating}
            style={[styles.tooltipContainer, { position: strategy, top: y ?? 0, left: x ?? 0 }, tooltipWidthStyle]}>
            <View
              style={[
                styles.tooltip,
                shouldWrapText && styles.tooltipWrapped,
                styles.tooltipAnimated,
                visible ? styles.tooltipVisible : styles.tooltipHidden,
                !visible && hiddenTranslateStyle,
              ]}>
              <CustomText
                style={[styles.tooltipText, shouldWrapText && styles.tooltipTextWrapped]}
                text={tooltipContent}
                useTranslationText={false}
                {...tooltipTextProps}
              />
              {showArrow && <View style={arrowStyle} />}
            </View>
          </View>
        </Portal>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  tooltipContainer: {
    position: "absolute",
    zIndex: 1000,
  },
  tooltip: {
    backgroundColor: "#000B33",
    padding: 8,
    borderRadius: 8,
  },
  tooltipWrapped: {
    maxWidth: "100%",
    minWidth: 0,
  },
  tooltipAnimated: {
    transitionProperty: "opacity, transform",
    transitionDuration: `${TOOLTIP_ANIMATION_DURATION_MS}ms`,
    transitionTimingFunction: "ease-out",
  },
  tooltipVisible: {
    opacity: 1,
    transform: [{ translateX: 0 }, { translateY: 0 }],
  },
  tooltipHidden: {
    opacity: 0,
  },
  tooltipText: {
    color: "#fff",
    fontSize: 12,
    fontFamily: "Inter",
    fontWeight: 600,
    lineHeight: 16,
  },
  tooltipTextWrapped: {
    flexShrink: 1,
    flexWrap: "wrap",
    overflowWrap: "break-word",
    maxWidth: "100%",
    minWidth: 0,
    textAlign: "center",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  triangleDown: {
    position: "absolute",
    bottom: -8,
    left: "50%",
    marginLeft: -5,
    width: 0,
    height: 0,
    borderLeftWidth: 5,
    borderRightWidth: 5,
    borderTopWidth: 8,
    borderLeftColor: "transparent",
    borderRightColor: "transparent",
    borderTopColor: "#000B33",
  },
  triangleUp: {
    position: "absolute",
    top: -8,
    left: "50%",
    marginLeft: -5,
    width: 0,
    height: 0,
    borderLeftWidth: 5,
    borderRightWidth: 5,
    borderBottomWidth: 8,
    borderLeftColor: "transparent",
    borderRightColor: "transparent",
    borderBottomColor: "#000B33",
  },
  triangleLeft: {
    position: "absolute",
    left: -8,
    top: "50%",
    marginTop: -5,
    width: 0,
    height: 0,
    borderTopWidth: 5,
    borderBottomWidth: 5,
    borderRightWidth: 8,
    borderTopColor: "transparent",
    borderBottomColor: "transparent",
    borderRightColor: "#000B33",
  },
  triangleRight: {
    position: "absolute",
    right: -8,
    top: "50%",
    marginTop: -5,
    width: 0,
    height: 0,
    borderTopWidth: 5,
    borderBottomWidth: 5,
    borderLeftWidth: 8,
    borderTopColor: "transparent",
    borderBottomColor: "transparent",
    borderLeftColor: "#000B33",
  },
});

export default Tooltip;
